<template>
    <BaseSection
        section-width="page"
        class="content-overview"
    >
        <template v-if="manual">
            <BaseGrid
                :gap="'var(--grid-gap-lg)'"
                class="base-grid--preset-1"
            >
                <ContentCard v-for="entry in manuallySelectedEntries" :key="entry.id" :entry />
            </BaseGrid>
        </template>
        <template v-else-if="infinite">
            <InfiniteScroll
                :is-loading="isFetching"
                :disabled="isFetching || endOfContentReached"
                @load-more="loadMoreContent"
            >
                <BaseGrid
                    :gap="'var(--grid-gap-lg)'"
                >
                    <ContentCard v-for="entry in content" :key="entry.id" :entry />
                    <div v-if="content.length === 0" class="content-overview__no-results">
                        <h2>Geen resultaten voor de geselecteerde filter(s)</h2>
                    </div>
                </BaseGrid>
            </InfiniteScroll>
        </template>
        <template v-else>
            <BaseGrid
                :gap="'var(--grid-gap-lg)'"
                class="base-grid--preset-1"
            >
                <BaseLoader v-if="isFetching" />
                <template v-else-if="content.length > 0">
                    <ContentCard v-for="entry in content" :key="entry.id" :entry />
                </template>
                <div v-else class="content-overview__no-results">
                    <h2>Geen resultaten voor de geselecteerde filter(s)</h2>
                </div>
            </BaseGrid>
            <BasePaginator
                v-model:current-page="currentPage"
                :total-pages="totalPages"
            />
        </template>
    </BaseSection>
</template>

<script setup>
import RelatedContentQuery from '~/graphql/queries/RelatedContent.graphql';

const props = defineProps({
    manual: {
        type: Boolean,
        default: false
    },
    manuallySelectedEntries: {
        type: Array,
        default: () => []
    },
    infinite: {
        type: Boolean,
        default: false
    },
    sections: {
        type: String,
        default: undefined
    },
    limit: {
        type: Number,
        default: null
    },
    relatedTo: {
        type: Array,
        default: undefined
    },
    notRelatedTo: {
        type: Array,
        default: undefined
    },
    exclude: {
        type: Array,
        default: undefined
    },
    isNotPast: {
        type: Array,
        default: undefined
    },
    isBetween: {
        type: Array,
        default: undefined
    },
    orderBy: {
        type: String,
        default: undefined
    },
    fetchLimit: {
        type: Number,
        default: 10,
    },
    pageSize: {
        type: Number,
        default: 10
    }
});

const { exclude, manual, manuallySelectedEntries, sections, limit, relatedTo, notRelatedTo, isNotPast, isBetween, orderBy, fetchLimit, pageSize } = toRefs(props);

const { locale: activeLocale } = useI18n();

const currentPage = defineModel('currentPage', {
    type: Number,
    default: () => useCurrentPage()
});

const offset = computed(() => {
    return (currentPage.value - 1) * pageSize.value;
});

const variables = computed(() => ({
    site: activeLocale.value,
    section: sections.value,
    limit: limit.value || fetchLimit.value,
    relatedTo: relatedTo.value,
    notRelatedTo: notRelatedTo.value,
    offset: offset.value,
    isNotPast: isNotPast.value,
    orderBy: orderBy.value, // fixme: this doesn't work cos nested fields can't be queried
    isBetween: isBetween.value,
    ids: exclude.value?.length ? ['not', ...(exclude.value ?? [])] : undefined,
    // fixme: add other daterange options
}));

const { data, status } = await useAsyncQuery({
    query: RelatedContentQuery,
    variables
});

const endOfContentReached = computed(() => {
    return fetchLimit.value >= data?.value?.entryCount;
});

const isFetching = computed(() => {
    return status.value === 'pending';
});

const content = computed(() => {
    return data?.value?.entries ?? [];
});

const totalPages = computed(() => {
    return Math.ceil(data?.value?.entryCount / pageSize.value);
});

function loadMoreContent() {
    if (!endOfContentReached.value) {
        fetchLimit.value += pageSize.value;
    }
}
</script>

<style lang="less" src="./ContentOverview.less" />
